import noise3D from './../noise3D'

export default /* glsl */ `
  uniform sampler2D uTexture;
  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  uniform vec3 uColor3;
  uniform float uColor0Weight;
  uniform float uColor1Weight;
  uniform float uColor2Weight;
  uniform float uColor3Weight;
  uniform float uRegionScale;
  uniform float uDetailScale;
  uniform float uDetailAmount;
  uniform float uWarpAmount;
  uniform float uSoftness;
  uniform float uXOffset;
  uniform float uYOffset;
  uniform vec3 uDotColor;
  uniform float uDotOpacity;
  uniform float uAspect;
  uniform vec4 uProgress;
  uniform vec3 uBgColor;
  varying vec2 vUv;

  ${noise3D}

  float noise2(vec2 p) {
    // 'snoise' returns a value in -1..1, so we remap it to 0..1
    return snoise(vec3(p, 0.0)) * 0.5 + 0.5;
  }

  float biasNoise(float noise, float weightA, float weightB) {
    float total = weightA + weightB;
    if (total <= 0.0) return 0.5;

    float split = weightA / total;
    // Remap so [0, split] stretches to [0, 0.5] and [split, 1] to [0.5, 1]
    if (noise < split) {
      return split == 0.0 ? 0.5 : (noise / split) * 0.5;
    }

    return split == 1.0 ? 0.5 : 0.5 + ((noise - split) / (1.0 - split)) * 0.5;
  }

  vec3 pearlGradient(vec2 uv) {
    vec2 p = vec2(uv.x * uAspect + uXOffset, uv.y + uYOffset);

    // Add some warping
    float warpX = noise2(p * uRegionScale + vec2(13.1, 7.7)) - 0.5;
    float warpY = noise2(p * uRegionScale + vec2(91.3, 41.2)) - 0.5;
    p += vec2(warpX, warpY) * uWarpAmount;

    // Low-frequency noise so neighbouring pixels make similar
    // blend decisions and produce coherent colour zones
    float regionPairA = noise2(p * uRegionScale);
    float regionPairB = noise2(p * uRegionScale + vec2(50.0, 50.0));
    float regionBlend = noise2(p * uRegionScale + vec2(100.0, 100.0));

    // High-frequency noise so each zone gets local colour variation
    float detailPairA = noise2(p * uDetailScale + vec2(200.0, 200.0));
    float detailPairB = noise2(p * uDetailScale * 1.3 + vec2(300.0, 300.0));
    float detailBlend = noise2(p * uDetailScale + vec2(400.0, 400.0));

    // Combine each region with its detail
    float noisePairA = clamp(regionPairA + (detailPairA - 0.5) * uDetailAmount, 0.0, 1.0);
    float noisePairB = clamp(regionPairB + (detailPairB - 0.5) * uDetailAmount, 0.0, 1.0);
    float noiseBlend = clamp(regionBlend + (detailBlend - 0.5) * uDetailAmount, 0.0, 1.0);

    float smoothstepLow = (1.0 - uSoftness) * 0.5;
    float smoothstepHigh = 1.0 - smoothstepLow;

    // Get weighted noise values to blend the 4 colours for this pixel
    float noiseAWeighted = biasNoise(noisePairA, uColor0Weight, uColor1Weight);
    vec3 colorA = mix(uColor0, uColor1, smoothstep(smoothstepLow, smoothstepHigh, noiseAWeighted));

    float noiseBWeighted = biasNoise(noisePairB, uColor2Weight, uColor3Weight);
    vec3 colorB = mix(uColor2, uColor3, smoothstep(smoothstepLow, smoothstepHigh, noiseBWeighted));

    float noiseBlendWeighted = biasNoise(noiseBlend, uColor0Weight + uColor1Weight, uColor2Weight + uColor3Weight);
    vec3 color = mix(colorA, colorB, smoothstep(smoothstepLow, smoothstepHigh, noiseBlendWeighted));
    return color;
  }

  void main() {
    vec3 rec709 = vec3(0.2126, 0.7152, 0.0722);
    vec3 data = texture2D(uTexture, vUv).rgb;
    float grayscale = data.r;
    float fadein = data.g;

    vec3 gradient = pearlGradient(vUv);

    // Luminance-based dithering effect dot falloff to match the noise patterns
    float luminance = dot(gradient, rec709);
    float dotFalloff = smoothstep(0.8, 0.95, luminance);

    vec3 color = mix(gradient, uDotColor + vUv.y, grayscale * dotFalloff * uProgress.z * uDotOpacity);
    color = mix(uBgColor, color, (uProgress.x * uProgress.x));

    gl_FragColor = vec4(color, 1.0);
  }
`
